import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useWebSocket from '../../hooks/useWebSocket';
import useNotificationPolling from '../../hooks/useNotificationPolling';
import { setPollingEnabled } from '../../store/slices/notificationsSlice';

const WebSocketManager = () => {
  const dispatch = useDispatch();
  // Consider user authenticated if an access token exists in auth slice
  const isAuthenticated = useSelector(state => !!state.auth.tokens?.access);

  // Initialize WebSocket only once at the top level
  const webSocket = useWebSocket();
  const warmupTimeoutRef = useRef(null);
  const lastExplicitConnectAtRef = useRef(0);
  const lastWarmupAtRef = useRef(0);

  // Freshness fallback: the websocket is on-demand (sign-in warmup + open
  // dropdown), so while the user browses with the bell closed the socket is
  // deliberately down. The polling hook covers exactly that gap — it runs only
  // when pollingEnabled && the socket is disconnected/error, at its built-in
  // backoff cadence (60s → max 5min). Enabled only while authenticated so the
  // login screen never polls.
  useNotificationPolling();
  useEffect(() => {
    dispatch(setPollingEnabled(isAuthenticated));
  }, [isAuthenticated, dispatch]);

  // On-demand WebSocket management via window events
  useEffect(() => {
    const handleConnect = () => {
      lastExplicitConnectAtRef.current = Date.now();
      webSocket.connect();
    };

    const handleDisconnect = () => {
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
        warmupTimeoutRef.current = null;
      }
      webSocket.disconnect();
    };

    const handleWarmup = (e) => {
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
        warmupTimeoutRef.current = null;
      }
      lastWarmupAtRef.current = Date.now();
      webSocket.connect();
      const durationMs = e?.detail?.durationMs ?? 10000;
      warmupTimeoutRef.current = setTimeout(() => {
        // Only disconnect if no explicit connect happened after warmup started
        if (lastExplicitConnectAtRef.current <= lastWarmupAtRef.current) {
          webSocket.disconnect();
        }
        warmupTimeoutRef.current = null;
      }, durationMs);
    };

    window.addEventListener('notifications:connect', handleConnect);
    window.addEventListener('notifications:disconnect', handleDisconnect);
    window.addEventListener('notifications:warmup', handleWarmup);

    // If user is unauthenticated, ensure connection is closed
    if (!isAuthenticated) {
      webSocket.disconnect();
    }

    return () => {
      window.removeEventListener('notifications:connect', handleConnect);
      window.removeEventListener('notifications:disconnect', handleDisconnect);
      window.removeEventListener('notifications:warmup', handleWarmup);
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
        warmupTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, webSocket]);

  // This component doesn't render anything, it just manages the connection
  return null;
};

export default WebSocketManager; 